import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, BookOpen, Play, Clock, CheckCircle, XCircle,
  ArrowRight, Loader2, Trophy, Zap, Flame, Star, Target,
  Hourglass, Lock, LogOut, X
} from 'lucide-react';
import BrandHeader from '../components/BrandHeader';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOCK_ROUND_DURATION_SECONDS = 120; // 2 min per round for mock
const MOCK_BREAK_SECONDS = 15; // 15 sec break for mock

const ROUND_NAMES = {
  1: 'Mathematics', 2: 'Science', 3: 'Language Arts', 4: 'Logic & Puzzles',
  5: 'General Knowledge', 6: 'Current Affairs', 7: 'Reasoning', 8: 'Riddle Sprint (Final)'
};

const ExitMockDialog = ({ onCancel, onConfirm, fromLobby }) => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 z-50 animate-fade-in-up" data-testid="mock-exit-dialog">
    <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-[#fef2f2] rounded-2xl flex items-center justify-center">
          <X className="w-6 h-6 text-[#dc2626]" />
        </div>
        <h2 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Exit Mock Competition?</h2>
      </div>
      <p className="text-[#64748b] mb-6">
        Your current progress will be lost and your score will not be saved. You can start a new mock any time.
      </p>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} className="btn-secondary" data-testid="mock-exit-cancel">Keep Going</button>
        <button onClick={onConfirm} className="px-5 py-2.5 rounded-xl font-bold bg-[#dc2626] hover:bg-[#b91c1c] text-white shadow-[0_4px_0_#7f1d1d] transition-all" data-testid="mock-exit-confirm">
          {fromLobby ? 'Exit to Lobby' : 'Exit'}
        </button>
      </div>
    </div>
  </div>
);

const PracticeZonePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Access
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessInput, setAccessInput] = useState('');
  const [participantInfo, setParticipantInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Mode selection
  const [mode, setMode] = useState(null); // 'practice' | 'mock'

  // Practice mode
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [practiceQuestions, setPracticeQuestions] = useState([]);
  const [practiceIndex, setPracticeIndex] = useState(0);
  const [practiceAnswer, setPracticeAnswer] = useState(null);
  const [practiceSubmitted, setPracticeSubmitted] = useState(false);
  const [practiceResult, setPracticeResult] = useState(null);
  const [practiceScore, setPracticeScore] = useState({ correct: 0, total: 0 });
  const [practiceComplete, setPracticeComplete] = useState(false);

  // Mock competition mode
  const [mockRound, setMockRound] = useState(0);
  const [mockQuestions, setMockQuestions] = useState([]);
  const [mockIndex, setMockIndex] = useState(0);
  const [mockAnswer, setMockAnswer] = useState(null);
  const [mockSubmitted, setMockSubmitted] = useState(false);
  const [mockResult, setMockResult] = useState(null);
  const [mockScore, setMockScore] = useState(0);
  const [mockStreak, setMockStreak] = useState(0);
  const [mockTime, setMockTime] = useState(0);
  const [mockInWaiting, setMockInWaiting] = useState(false);
  const [mockWaitTime, setMockWaitTime] = useState(0);
  const [mockRoundFinished, setMockRoundFinished] = useState(false);
  const [mockRoundScore, setMockRoundScore] = useState(0);
  const [mockComplete, setMockComplete] = useState(false);
  const [mockStarted, setMockStarted] = useState(false);
  const [mockCountdown, setMockCountdown] = useState(0);
  const hasAutoFinished = useRef(false);

  // Exit Mock confirmation
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Did the participant come from the Competition Lobby?
  const fromLobby = !!searchParams.get('pid');

  const resetMockState = () => {
    setMockStarted(false); setMockRound(0); setMockQuestions([]); setMockIndex(0);
    setMockAnswer(null); setMockSubmitted(false); setMockResult(null);
    setMockScore(0); setMockStreak(0); setMockTime(0);
    setMockInWaiting(false); setMockWaitTime(0);
    setMockRoundFinished(false); setMockRoundScore(0);
    setMockComplete(false); setMockCountdown(0);
    hasAutoFinished.current = false;
    setShowExitConfirm(false);
  };

  // Smart back: return to lobby if that's where we came from, else go to mode select
  const goBackFromMode = () => {
    resetMockState();
    setSelectedCategory(null); setPracticeQuestions([]); setPracticeComplete(false);
    if (fromLobby) { navigate('/lobby'); return; }
    setMode(null);
  };

  const confirmExitMock = () => {
    resetMockState();
    if (fromLobby) { navigate('/lobby'); return; }
    setMode(null);
  };

  useEffect(() => { loadCategories(); }, []);

  // Auto-access if pid query param is provided (e.g. from Competition Lobby)
  useEffect(() => {
    const pid = searchParams.get('pid');
    const presetMode = searchParams.get('mode');
    if (!pid || accessGranted) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API_URL}/api/practice/access`, { participant_id: pid });
        setAccessGranted(true);
        setParticipantInfo(res.data);
        if (presetMode === 'practice' || presetMode === 'mock') setMode(presetMode);
      } catch (err) {
        setError(err.response?.data?.detail || 'Auto-access failed. Please enter your Participant ID.');
      } finally { setLoading(false); }
    })();
  }, [searchParams, accessGranted]);

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/practice/categories`);
      setCategories(res.data.categories);
    } catch (e) {}
  };

  const handleAccess = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const isEmail = accessInput.includes('@');
      const res = await axios.post(`${API_URL}/api/practice/access`, { [isEmail ? 'email' : 'participant_id']: accessInput });
      setAccessGranted(true);
      setParticipantInfo(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Access denied');
    } finally { setLoading(false); }
  };

  // ===== PRACTICE MODE =====
  const startPractice = async (cat) => {
    setSelectedCategory(cat); setLoading(true);
    setPracticeQuestions([]); setPracticeIndex(0);
    setPracticeScore({ correct: 0, total: 0 }); setPracticeComplete(false);
    try {
      const res = await axios.get(`${API_URL}/api/practice/questions/${cat.id}?limit=5`);
      setPracticeQuestions(res.data.questions);
    } catch (e) { setError('Failed to load questions'); }
    finally { setLoading(false); }
  };

  const submitPracticeAnswer = async () => {
    if (!practiceAnswer || practiceSubmitted) return;
    try {
      const res = await axios.post(`${API_URL}/api/practice/submit-answer`, {
        question_id: practiceQuestions[practiceIndex].id, selected_option: practiceAnswer
      });
      setPracticeResult(res.data);
      setPracticeSubmitted(true);
      setPracticeScore(s => ({ correct: s.correct + (res.data.is_correct ? 1 : 0), total: s.total + 1 }));
    } catch (e) { setError('Failed to submit'); }
  };

  const nextPracticeQ = () => {
    if (practiceIndex < practiceQuestions.length - 1) {
      setPracticeIndex(i => i + 1); setPracticeAnswer(null); setPracticeSubmitted(false); setPracticeResult(null);
    } else { setPracticeComplete(true); }
  };

  // ===== MOCK COMPETITION MODE =====
  const startMockCompetition = () => {
    setMockStarted(true); setMockCountdown(3);
    setMockScore(0); setMockStreak(0); setMockComplete(false);
  };

  // Countdown to start
  useEffect(() => {
    if (mockCountdown <= 0) return;
    const t = setTimeout(() => {
      if (mockCountdown === 1) { loadMockRound(1); }
      setMockCountdown(c => c - 1);
    }, 1000);
    return () => clearTimeout(t);
  }, [mockCountdown]);

  const loadMockRound = async (roundNum) => {
    setMockRound(roundNum); setLoading(true);
    setMockQuestions([]); setMockIndex(0); setMockAnswer(null);
    setMockSubmitted(false); setMockResult(null);
    setMockRoundFinished(false); setMockRoundScore(0);
    setMockInWaiting(false); hasAutoFinished.current = false;
    const duration = roundNum === 8 ? 60 : MOCK_ROUND_DURATION_SECONDS;
    setMockTime(duration);
    try {
      const res = await axios.get(`${API_URL}/api/practice/mock-round/${roundNum}?limit=${roundNum === 8 ? 10 : 5}`);
      setMockQuestions(res.data.questions);
    } catch (e) { setError('Failed to load round'); }
    finally { setLoading(false); }
  };

  // Mock timer
  useEffect(() => {
    if (!mockStarted || mockInWaiting || mockRoundFinished || mockComplete || mockTime <= 0 || mockCountdown > 0) return;
    const t = setInterval(() => {
      setMockTime(prev => {
        const next = Math.max(0, prev - 1);
        if (next === 0 && !hasAutoFinished.current) {
          hasAutoFinished.current = true;
          finishMockRound();
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [mockStarted, mockInWaiting, mockRoundFinished, mockComplete, mockTime, mockCountdown]);

  // Mock waiting room countdown
  useEffect(() => {
    if (!mockInWaiting) return;
    const t = setInterval(() => {
      setMockWaitTime(prev => {
        if (prev <= 1) {
          clearInterval(t);
          if (mockRound < 8) { loadMockRound(mockRound + 1); }
          else { setMockComplete(true); setMockInWaiting(false); }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [mockInWaiting, mockRound]);

  const submitMockAnswer = () => {
    if (!mockAnswer || mockSubmitted) return;
    const q = mockQuestions[mockIndex];
    const correct = mockAnswer === q.correct_option;
    const pts = correct ? 10 : 0;
    const newStreak = correct ? mockStreak + 1 : 0;
    const streakBonus = newStreak >= 7 ? 15 : newStreak >= 5 ? 10 : newStreak >= 3 ? 5 : 0;
    const total = pts + (correct && streakBonus > 0 ? streakBonus : 0);

    setMockResult({ is_correct: correct, correct_option: q.correct_option, points_earned: total, streak_bonus: streakBonus });
    setMockSubmitted(true);
    setMockScore(s => s + total);
    setMockRoundScore(s => s + total);
    setMockStreak(newStreak);
  };

  const nextMockQ = () => {
    if (mockIndex < mockQuestions.length - 1) {
      setMockIndex(i => i + 1); setMockAnswer(null); setMockSubmitted(false); setMockResult(null);
    }
  };

  const finishMockRound = () => {
    if (mockRoundFinished) return;
    setMockRoundFinished(true);
    setMockInWaiting(true);
    setMockWaitTime(MOCK_BREAK_SECONDS);
  };

  // ===== OPTION STYLE (shared) =====
  const getOptStyle = (option, selected, submitted, result) => {
    const base = "w-full text-left rounded-2xl p-4 sm:p-5 font-semibold transition-all border-2";
    if (submitted && result) {
      if (option === result.correct_option) return `${base} bg-[#f0fdf4] border-[#22c55e] text-[#15803d] shadow-[0_4px_0_#22c55e]`;
      if (option === selected && !result.is_correct) return `${base} bg-[#fef2f2] border-[#ef4444] text-[#b91c1c] shadow-[0_4px_0_#ef4444]`;
      return `${base} bg-white border-[#e2e8f0] text-[#64748b]`;
    }
    if (option === selected) return `${base} bg-[#eff6ff] border-[#2565eb] text-[#2565eb] shadow-[0_4px_0_#2565eb]`;
    return `${base} bg-white border-[#e2e8f0] text-[#0f172a] hover:border-[#2565eb] hover:bg-[#eff6ff] cursor-pointer`;
  };

  // ===== RENDER: ACCESS GATE =====
  if (!accessGranted) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
        <BrandHeader />
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#2565eb] rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-[0_6px_0_#1e3a8a]">
                <BookOpen className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Practice Zone</h1>
              <p className="text-[#64748b] mt-2">Enter your Participant ID or parent email</p>
            </div>
            {error && <div className="bg-[#fef2f2] border-2 border-[#fecaca] rounded-2xl p-4 mb-4 text-[#dc2626] text-sm">{error}</div>}
            <form onSubmit={handleAccess} className="space-y-4">
              <input type="text" value={accessInput} onChange={e => setAccessInput(e.target.value)} className="input-olympiad" placeholder="Participant ID or email" data-testid="practice-access-input" />
              <button type="submit" disabled={loading || !accessInput} className="btn-primary w-full disabled:opacity-50" data-testid="practice-access-btn">
                {loading ? 'Checking...' : 'Enter Practice Zone'}
              </button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  // ===== RENDER: MODE SELECTION =====
  if (!mode) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
        <header className="p-6 flex items-center justify-between border-b border-[#e2e8f0]">
          <button onClick={() => fromLobby ? navigate('/lobby') : setAccessGranted(false)} className="flex items-center gap-2 text-[#64748b] hover:text-[#0f172a]" data-testid="mode-back-btn"><ArrowLeft className="w-5 h-5" /> {fromLobby ? 'Back to Lobby' : 'Back'}</button>
          <span className="text-lg font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Welcome, {participantInfo?.child_name}!</span>
        </header>
        <main className="flex-1 p-6 max-w-3xl mx-auto w-full flex flex-col items-center justify-center">
          <h1 className="text-3xl font-bold text-[#0f172a] mb-8 text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>Choose Your Mode</h1>
          <div className="grid md:grid-cols-2 gap-6 w-full max-w-xl">
            <button onClick={() => setMode('practice')} className="card-olympiad p-8 text-center hover:border-[#2565eb] transition-all group" data-testid="mode-practice">
              <Target className="w-12 h-12 text-[#2565eb] mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>Practice Quiz</h2>
              <p className="text-sm text-[#64748b]">Pick a category and practice at your own pace. No timer.</p>
            </button>
            <button onClick={() => setMode('mock')} className="card-olympiad p-8 text-center hover:border-[#22c55e] transition-all group" data-testid="mode-mock">
              <Trophy className="w-12 h-12 text-[#22c55e] mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>Mock Competition</h2>
              <p className="text-sm text-[#64748b]">8 rounds with timers, just like the real Olympiad. Test yourself!</p>
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ===== RENDER: PRACTICE MODE =====
  if (mode === 'practice') {
    // Category selection
    if (!selectedCategory) {
      return (
        <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
          <header className="p-6 flex items-center justify-between border-b border-[#e2e8f0]">
            <button onClick={goBackFromMode} className="flex items-center gap-2 text-[#64748b] hover:text-[#0f172a]" data-testid="practice-back-btn"><ArrowLeft className="w-5 h-5" /> {fromLobby ? 'Back to Lobby' : 'Back'}</button>
            <span className="text-lg font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Practice Quiz</span>
          </header>
          <main className="flex-1 p-6 max-w-3xl mx-auto w-full">
            <h2 className="text-2xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>Choose a Category</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {categories.map(cat => (
                <button key={cat.id} onClick={() => startPractice(cat)} className="card-olympiad p-5 text-left hover:border-[#2565eb]" data-testid={`practice-cat-${cat.id}`}>
                  <h3 className="font-bold text-[#0f172a] mb-1">{cat.name}</h3>
                  <p className="text-xs text-[#64748b]">{cat.question_count} questions</p>
                </button>
              ))}
            </div>
          </main>
        </div>
      );
    }

    // Practice complete
    if (practiceComplete) {
      return (
        <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <Trophy className="w-20 h-20 text-[#face39] mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>Practice Complete!</h1>
            <p className="text-xl font-bold text-[#2565eb] mb-6">{practiceScore.correct} / {practiceScore.total} correct</p>
            <div className="flex gap-3 justify-center flex-wrap">
              {fromLobby && <button onClick={() => navigate('/lobby')} className="btn-secondary" data-testid="practice-complete-lobby-btn">Back to Lobby</button>}
              <button onClick={() => { setSelectedCategory(null); setPracticeComplete(false); }} className="btn-secondary">Try Another</button>
              <button onClick={() => startPractice(selectedCategory)} className="btn-primary">Retry</button>
            </div>
          </div>
        </div>
      );
    }

    // Practice quiz active
    if (loading || practiceQuestions.length === 0) {
      return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-12 h-12 text-[#2565eb] animate-spin" /></div>;
    }

    const pq = practiceQuestions[practiceIndex];
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
        <header className="p-4 bg-gradient-to-r from-[#2565eb] to-[#1e40af]">
          <div className="max-w-3xl mx-auto flex items-center justify-between text-white gap-3">
            <button onClick={() => { setSelectedCategory(null); setPracticeQuestions([]); setPracticeIndex(0); setPracticeAnswer(null); setPracticeSubmitted(false); setPracticeResult(null); setPracticeScore({correct:0,total:0}); if (fromLobby) navigate('/lobby'); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 hover:bg-white/30 text-sm font-semibold transition-all" data-testid="practice-active-exit-btn">
              <LogOut className="w-4 h-4" /> {fromLobby ? 'Exit' : 'Exit'}
            </button>
            <p className="font-bold truncate">{selectedCategory.name}</p>
            <p className="text-sm whitespace-nowrap">Q {practiceIndex + 1} / {practiceQuestions.length}</p>
            <p className="font-bold whitespace-nowrap">{practiceScore.correct} correct</p>
          </div>
        </header>
        <div className="px-4 py-2"><div className="max-w-3xl mx-auto"><Progress value={((practiceIndex + 1) / practiceQuestions.length) * 100} className="h-2" /></div></div>
        <main className="flex-1 p-4 sm:p-6 max-w-3xl mx-auto w-full">
          <div className="card-olympiad p-6 sm:p-8">
            <h2 className="text-xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>{pq.question_text}</h2>
            <div className="space-y-3">
              {['A', 'B', 'C', 'D'].map(opt => (
                <button key={opt} onClick={() => !practiceSubmitted && setPracticeAnswer(opt)} disabled={practiceSubmitted}
                  className={getOptStyle(opt, practiceAnswer, practiceSubmitted, practiceResult)}>
                  <span className="flex items-center gap-4">
                    <span className="w-10 h-10 rounded-xl bg-[#f1f5f9] flex items-center justify-center font-bold text-[#64748b]">{opt}</span>
                    <span className="flex-1">{pq[`option_${opt.toLowerCase()}`]}</span>
                    {practiceSubmitted && practiceResult?.correct_option === opt && <CheckCircle className="w-6 h-6 text-[#22c55e]" />}
                    {practiceSubmitted && opt === practiceAnswer && !practiceResult?.is_correct && <XCircle className="w-6 h-6 text-[#ef4444]" />}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end mt-6">
            {!practiceSubmitted ? (
              <button onClick={submitPracticeAnswer} disabled={!practiceAnswer} className="btn-primary flex items-center gap-2 disabled:opacity-50"><CheckCircle className="w-5 h-5" /> Submit</button>
            ) : (
              <button onClick={nextPracticeQ} className="btn-secondary flex items-center gap-2">{practiceIndex >= practiceQuestions.length - 1 ? 'See Results' : 'Next'} <ArrowRight className="w-5 h-5" /></button>
            )}
          </div>
        </main>
      </div>
    );
  }

  // ===== RENDER: MOCK COMPETITION MODE =====

  // Pre-start
  if (!mockStarted) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
        <header className="p-6 flex items-center justify-between border-b border-[#e2e8f0]">
          <button onClick={goBackFromMode} className="flex items-center gap-2 text-[#64748b] hover:text-[#0f172a]" data-testid="mock-prestart-back-btn"><ArrowLeft className="w-5 h-5" /> {fromLobby ? 'Back to Lobby' : 'Back'}</button>
          <span className="text-lg font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Mock Competition</span>
        </header>
        <main className="flex-1 p-6 max-w-3xl mx-auto w-full flex flex-col items-center justify-center">
          <Trophy className="w-20 h-20 text-[#22c55e] mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-[#0f172a] mb-4 text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>Ready for the Mock Olympiad?</h1>
          <p className="text-[#64748b] text-center mb-6 max-w-md">8 rounds, 5 questions each (10 in the final), 2 minutes per round. 15-second break between rounds.</p>
          <div className="grid grid-cols-4 gap-2 mb-8 w-full max-w-md">
            {[1,2,3,4,5,6,7,8].map(r => (
              <div key={r} className="bg-[#f1f5f9] rounded-xl p-3 text-center">
                <p className="text-xs text-[#64748b]">R{r}</p>
                <p className="text-xs font-bold text-[#0f172a] truncate">{ROUND_NAMES[r]}</p>
              </div>
            ))}
          </div>
          <button onClick={startMockCompetition} className="btn-primary flex items-center gap-2" data-testid="start-mock-btn">
            <Play className="w-5 h-5" /> Start Mock Competition
          </button>
        </main>
      </div>
    );
  }

  // Countdown
  if (mockCountdown > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#2565eb] to-[#1e3a8a] flex items-center justify-center relative">
        <button onClick={() => setShowExitConfirm(true)} className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm font-semibold backdrop-blur transition-all" data-testid="mock-countdown-exit-btn">
          <LogOut className="w-4 h-4" /> Exit
        </button>
        <div className="text-center animate-fade-in-up">
          <p className="text-white/80 text-xl mb-4">Get Ready!</p>
          <p className="text-9xl font-bold text-white" style={{ fontFamily: 'Fredoka, sans-serif' }}>{mockCountdown}</p>
        </div>
        {showExitConfirm && <ExitMockDialog onCancel={() => setShowExitConfirm(false)} onConfirm={confirmExitMock} fromLobby={fromLobby} />}
      </div>
    );
  }

  // Mock complete
  if (mockComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#2565eb] to-[#1e3a8a] flex items-center justify-center p-6">
        <div className="text-center max-w-md animate-fade-in-up">
          <div className="w-32 h-32 bg-[#face39] rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_8px_0_#d97706]">
            <Trophy className="w-16 h-16 text-[#0f172a]" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>Mock Complete!</h1>
          <div className="card-olympiad p-6 mb-8">
            <p className="text-sm text-[#64748b] mb-2">Total Score</p>
            <p className="text-4xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{mockScore} points</p>
          </div>
          <div className="flex gap-3 justify-center flex-wrap">
            {fromLobby && (
              <button onClick={() => { resetMockState(); navigate('/lobby'); }} className="btn-secondary" data-testid="mock-complete-lobby-btn">Back to Lobby</button>
            )}
            <button onClick={() => { setMode(null); setMockStarted(false); setMockRound(0); setMockScore(0); setMockComplete(false); }} className="btn-secondary" data-testid="mock-complete-menu-btn">Back to Menu</button>
            <button onClick={() => { setMockStarted(false); setMockRound(0); setMockScore(0); setMockStreak(0); setMockComplete(false); }} className="btn-primary" data-testid="mock-complete-retry-btn">Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  // Waiting room
  if (mockInWaiting) {
    const isLast = mockRound >= 8;
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
        <header className="p-6 bg-gradient-to-r from-[#2565eb] to-[#1e40af]">
          <div className="max-w-3xl mx-auto flex items-center justify-between text-white gap-3">
            <button onClick={() => setShowExitConfirm(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 hover:bg-white/30 text-sm font-semibold transition-all" data-testid="mock-waiting-exit-btn">
              <LogOut className="w-4 h-4" /> Exit
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-center flex-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>Round {mockRound} Complete!</h1>
            <div className="w-[72px]" />
          </div>
        </header>
        <main className="flex-1 p-6 max-w-3xl mx-auto w-full">
          <div className="card-olympiad p-6 mb-6 animate-fade-in-up">
            <h2 className="text-xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>Round {mockRound} Score</h2>
            <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
              <span className="text-[#64748b]">Round Points</span>
              <span className="text-2xl font-bold text-[#2565eb]">{mockRoundScore} pts</span>
            </div>
            <div className="flex justify-between items-center py-2 mt-2">
              <span className="font-bold text-[#0f172a]">Total Score</span>
              <span className="text-2xl font-bold text-[#0f172a]">{mockScore} pts</span>
            </div>
          </div>
          {!isLast ? (
            <div className="card-olympiad p-8 text-center animate-fade-in-up border-2 border-[#2565eb]" data-testid="mock-waiting-room">
              <Hourglass className="w-10 h-10 text-[#2565eb] mx-auto mb-4 animate-pulse" />
              <h3 className="text-lg font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>Waiting Room</h3>
              <p className="text-[#64748b] mb-4">Next round starts in</p>
              <p className="text-5xl font-bold font-mono text-[#2565eb] mb-2">{mockWaitTime}s</p>
              <p className="text-sm text-[#64748b]">Get ready for Round {mockRound + 1}: {ROUND_NAMES[mockRound + 1]}</p>
            </div>
          ) : (
            <div className="card-olympiad p-8 text-center border-2 border-[#22c55e]">
              <Trophy className="w-10 h-10 text-[#22c55e] mx-auto mb-4" />
              <p className="font-bold text-[#0f172a]">All rounds complete! Calculating final results...</p>
            </div>
          )}
        </main>
        {showExitConfirm && <ExitMockDialog onCancel={() => setShowExitConfirm(false)} onConfirm={confirmExitMock} fromLobby={fromLobby} />}
      </div>
    );
  }

  // Loading
  if (loading || mockQuestions.length === 0) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-12 h-12 text-[#2565eb] animate-spin" /></div>;
  }

  // ===== MOCK ACTIVE ROUND =====
  const mq = mockQuestions[mockIndex];
  const mockMin = Math.floor(mockTime / 60);
  const mockSec = mockTime % 60;
  const mockProgress = ((mockIndex + 1) / mockQuestions.length) * 100;
  const isLastMockQ = mockIndex >= mockQuestions.length - 1;
  const isR8 = mockRound === 8;

  return (
    <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
      <header className={`p-4 ${isR8 ? 'bg-gradient-to-r from-[#dc2626] to-[#991b1b]' : 'bg-gradient-to-r from-[#2565eb] to-[#1e40af]'}`}>
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 text-white min-w-0">
            <button onClick={() => setShowExitConfirm(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 hover:bg-white/30 text-sm font-semibold transition-all whitespace-nowrap" data-testid="mock-active-exit-btn">
              <LogOut className="w-4 h-4" /> Exit
            </button>
            <div className="min-w-0">
              <p className="text-xs opacity-80">Round {mockRound}</p>
              <p className="font-bold truncate text-sm">{ROUND_NAMES[mockRound]}</p>
            </div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${mockTime < 30 ? 'bg-[#ef4444] animate-pulse' : 'bg-white/20'}`}>
            <Clock className="w-5 h-5 text-white" />
            <span className="text-xl font-bold text-white font-mono">{mockMin}:{mockSec.toString().padStart(2, '0')}</span>
          </div>
          <div className="text-right text-white">
            <p className="text-xs opacity-80">Score</p>
            <p className="font-bold">{mockScore} pts</p>
          </div>
        </div>
      </header>

      <div className="px-4 py-2 bg-white border-b border-[#e2e8f0]">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-[#64748b]">Question {mockIndex + 1} of {mockQuestions.length}</span>
            {mockStreak >= 3 && <span className="flex items-center gap-1 text-[#ef4444] font-semibold"><Flame className="w-4 h-4" /> {mockStreak} streak!</span>}
          </div>
          <Progress value={mockProgress} className="h-2" />
        </div>
      </div>

      <main className="flex-1 p-4 sm:p-6 max-w-3xl mx-auto w-full">
        <div className="card-olympiad p-6 sm:p-8 animate-fade-in-up" key={`${mockRound}-${mockIndex}`}>
          <h2 className="text-xl sm:text-2xl font-bold text-[#0f172a] mb-8" style={{ fontFamily: 'Fredoka, sans-serif' }}>{mq.question_text}</h2>
          <div className="space-y-3">
            {['A', 'B', 'C', 'D'].map(opt => (
              <button key={opt} onClick={() => !mockSubmitted && setMockAnswer(opt)} disabled={mockSubmitted}
                className={getOptStyle(opt, mockAnswer, mockSubmitted, mockResult)} data-testid={`mock-opt-${opt.toLowerCase()}`}>
                <span className="flex items-center gap-4">
                  <span className="w-10 h-10 rounded-xl bg-[#f1f5f9] flex items-center justify-center font-bold text-[#64748b]">{opt}</span>
                  <span className="flex-1">{mq[`option_${opt.toLowerCase()}`]}</span>
                  {mockSubmitted && mockResult?.correct_option === opt && <CheckCircle className="w-6 h-6 text-[#22c55e]" />}
                  {mockSubmitted && opt === mockAnswer && !mockResult?.is_correct && <XCircle className="w-6 h-6 text-[#ef4444]" />}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-between mt-6">
          <button onClick={finishMockRound} className="px-5 py-2.5 rounded-xl font-bold border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#d97706] hover:text-[#d97706] transition-all text-sm" data-testid="mock-finish-round">
            Finish Round
          </button>
          {!mockSubmitted ? (
            <button onClick={submitMockAnswer} disabled={!mockAnswer} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="mock-submit-btn"><CheckCircle className="w-5 h-5" /> Submit</button>
          ) : (
            <button onClick={isLastMockQ ? finishMockRound : nextMockQ} className="btn-secondary flex items-center gap-2" data-testid="mock-next-btn">
              {isLastMockQ ? 'Finish Round' : 'Next'} <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {mockSubmitted && mockResult && (
          <div className={`mt-6 p-4 rounded-2xl border-2 animate-fade-in-up ${mockResult.is_correct ? 'bg-[#f0fdf4] border-[#22c55e]' : 'bg-[#fef2f2] border-[#ef4444]'}`}>
            <div className="flex items-center justify-between">
              <p className={`font-semibold flex items-center gap-2 ${mockResult.is_correct ? 'text-[#16a34a]' : 'text-[#dc2626]'}`}>
                {mockResult.is_correct ? <><CheckCircle className="w-5 h-5" /> Correct!</> : <><XCircle className="w-5 h-5" /> Incorrect</>}
              </p>
              <p className="font-bold text-[#0f172a]">+{mockResult.points_earned} pts</p>
            </div>
          </div>
        )}
      </main>
      {showExitConfirm && <ExitMockDialog onCancel={() => setShowExitConfirm(false)} onConfirm={confirmExitMock} fromLobby={fromLobby} />}
    </div>
  );
};

export default PracticeZonePage;
